import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import Button from "common/core/button";
import Icon from "common/core/icon";
import { useFeatureFlag } from "common/feature_gating";
import type { RetakeManager } from "common/identity_verification/retake/notary";

import { PhotoType, type IdentityDocumentViewerEngine } from "./engine";
import Styles from "./index.module.scss";

export type ActionProps = {
  retakeManager?: RetakeManager;
  engine: IdentityDocumentViewerEngine;
  isSimplifiedViewer?: boolean;
};

type RetakeButtonProps = {
  retakeManager: RetakeManager;
  currentPhotoType: IdentityDocumentViewerEngine["currentPhotoType"];
  singleCredentialRetake: boolean;
};

const MESSAGES = defineMessages({
  nextDocument: {
    id: "ae2ab2a2-ea56-46cf-968c-e7e2489eb0e1",
    defaultMessage: "Next document",
  },
  prevDocument: {
    id: "85dfc724-d88a-45cf-a146-6e8856baf60a",
    defaultMessage: "Previous document",
  },
  retakeId: {
    id: "a7e40d8b-a19c-495c-90cf-43055a61b028",
    defaultMessage: "Retake ID",
  },
  retakeDocument: {
    id: "e06eebc8-0c03-4dd9-bd71-5dead4b15516",
    defaultMessage: "Retake document",
  },
  retakeSelfie: {
    id: "0fcb4316-2acf-41aa-a43f-7155ef2526a4",
    defaultMessage: "Retake selfie",
  },
});

function RetakeButton(props: RetakeButtonProps) {
  const { retakeManager, currentPhotoType, singleCredentialRetake } = props;
  const intl = useIntl();
  return singleCredentialRetake ? (
    <Button
      buttonColor="action"
      variant="secondary"
      onClick={() => retakeManager.initiate({ individualRetake: currentPhotoType })}
      disabled={retakeManager.inProgress}
    >
      {currentPhotoType === PhotoType.Primary
        ? intl.formatMessage(MESSAGES.retakeId)
        : currentPhotoType === PhotoType.Secondary
          ? intl.formatMessage(MESSAGES.retakeDocument)
          : intl.formatMessage(MESSAGES.retakeSelfie)}
    </Button>
  ) : (
    <Button
      buttonColor="action"
      variant="secondary"
      onClick={retakeManager.initiate}
      disabled={retakeManager.inProgress}
    >
      <FormattedMessage id="2cd45bc5-3b8e-42ef-9929-6fc05f1d80c4" defaultMessage="Prompt retake" />
    </Button>
  );
}

export default function Actions(props: ActionProps) {
  const { retakeManager, engine, isSimplifiedViewer } = props;
  const intl = useIntl();
  const singleCredentialRetake = useFeatureFlag("single-credential-retake");
  const {
    currentPhoto,
    currentPhotoIndex,
    currentPhotoType,
    totalPhotoCount,
    isFront,
    onChangeDocumentSide,
    onPreviousDocument,
    onNextDocument,
  } = engine;
  const frontPicture = currentPhoto?.frontPicture;
  const backPicture = currentPhoto?.backPicture;
  const miniSquee = isFront ? backPicture : frontPicture;

  return (
    <div className={Styles.actions}>
      <div className={Styles.alternativeSideId}>
        {backPicture && (
          <Button buttonColor="dark" variant="tertiary" onClick={onChangeDocumentSide}>
            <img key={miniSquee} src={miniSquee!} alt="" />
            <FormattedMessage
              id="7b83bb72-6ad5-4378-963e-489bd7f7dcee"
              defaultMessage="View {isFront, select, true{back} other{front}}"
              values={{ isFront }}
            />
          </Button>
        )}
      </div>
      <div className={Styles.switchDocuments}>
        <Button
          variant="tertiary"
          buttonColor="action"
          onClick={onPreviousDocument}
          disabled={retakeManager?.inProgress || !onPreviousDocument}
          aria-label={intl.formatMessage(MESSAGES.prevDocument)}
        >
          <Icon name="caret-left" />
        </Button>
        <FormattedMessage
          id="1f74bead-6564-4f35-b537-144dfe2d3711"
          defaultMessage="{currentPhotoIndex} of {totalPhotoCount}"
          values={{ currentPhotoIndex: currentPhotoIndex + 1, totalPhotoCount }}
        />
        <Button
          variant="tertiary"
          buttonColor="action"
          onClick={onNextDocument}
          disabled={retakeManager?.inProgress || !onNextDocument}
          aria-label={intl.formatMessage(MESSAGES.nextDocument)}
        >
          <Icon name="caret-right" />
        </Button>
      </div>
      <div>
        {!isSimplifiedViewer && retakeManager && (
          <RetakeButton
            singleCredentialRetake={singleCredentialRetake}
            retakeManager={retakeManager}
            currentPhotoType={currentPhotoType}
          />
        )}
      </div>
    </div>
  );
}
